import React from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { startOfDay, endOfDay } from 'date-fns';

import { PinnedFilters } from 'components/common/PinnedFilters/PinnedFilters';
import { selectTradeTicketsPinnedFilters } from '@store';
import { useAssignees } from '@hooks';
import { URLS } from 'layouts/DrawerLayout/constants';
import {
  TRADE_TICKETS_CONTRACT_PERIODS,
  TRADE_TICKETS_STAGES_NAMES,
} from '../constants';

const TradeTicketsFilters = ({ viewMode }) => {
  const router = useRouter();
  const pinnedFilters = useSelector(selectTradeTicketsPinnedFilters);
  const { assigneesWithNone } = useAssignees();

  const stageOptions = Object.entries(TRADE_TICKETS_STAGES_NAMES).map(
    ([key, val]) => ({
      key,
      text: val.label,
      value: val.value,
    })
  );

  const contractPeriodOptions = Object.entries(
    TRADE_TICKETS_CONTRACT_PERIODS
  ).map(([key, val]) => ({
    key,
    text: val.label,
    value: key,
  }));

  const getNormalizedFilter = (filterName, options) => {
    const queryValue = router.query?.[filterName];
    if (!queryValue) return [];

    const values = Array.isArray(queryValue)
      ? queryValue
      : queryValue.split(',');

    return values
      .map((value) =>
        options.find((option) => String(option.value) === String(value))
      )
      .filter(Boolean);
  };

  const handleChangeMultiFilter = (val, filterName) => {
    const newQuery = { ...router.query };
    newQuery.page = 1;

    if (val.length) {
      newQuery[filterName] = val.map((item) => item.value).join(',');
    } else {
      delete newQuery[filterName];
    }

    router.push({
      pathname: URLS.tradeTickets,
      query: newQuery,
    });
  };

  const handleChangeSingleFilter = (value, filterName) => {
    const newQuery = { ...router.query };
    newQuery.page = 1;

    if (value !== null && value !== undefined && value !== '') {
      newQuery[filterName] = value;
    } else {
      delete newQuery[filterName];
    }

    router.push({
      pathname: URLS.tradeTickets,
      query: newQuery,
    });
  };

  const handleDateRangeChange = (newValue) => {
    const newQuery = { ...router.query };
    newQuery.page = 1;

    if (newValue && newValue[0]) {
      const fromDate = startOfDay(new Date(newValue[0]));
      newQuery.dateFrom = fromDate.toISOString();

      if (newValue[1]) {
        const toDate = endOfDay(new Date(newValue[1]));
        newQuery.dateTo = toDate.toISOString();
      } else {
        delete newQuery.dateTo;
      }
    } else {
      delete newQuery.dateFrom;
      delete newQuery.dateTo;
    }

    router.push({
      pathname: URLS.tradeTickets,
      query: newQuery,
    });
  };

  const FILTERS_CONFIG = [
    ...(viewMode === 'list'
      ? [
          {
            key: 'stage',
            type: 'filterMultiselect',
            isMulti: true,
            label: 'Stage',
            width: '250px',
            minWidth: '250px',
            hasSelectAllOption: true,
            value: getNormalizedFilter('stage', stageOptions),
            options: stageOptions,
          },
        ]
      : []),
    {
      key: 'contractPeriod',
      type: 'filterMultiselect',
      isMulti: true,
      label: 'Contract Period',
      width: '250px',
      minWidth: '200px',
      hasSelectAllOption: true,
      value: getNormalizedFilter('contractPeriod', contractPeriodOptions),
      options: contractPeriodOptions,
    },
    {
      key: 'assignee',
      type: 'filterMultiselect',
      isMulti: true,
      label: 'Assignees',
      width: '200px',
      minWidth: '300px',
      limitTags: 1,
      hasSelectAllOption: true,
      value: getNormalizedFilter('assignee', assigneesWithNone),
      options: assigneesWithNone,
    },
    {
      key: 'createdAt',
      type: 'dateRangePicker',
      startText: 'Created From',
      endText: 'Created To',
      label: 'Created At',
      width: '200px',
      queryKeys: ['dateFrom', 'dateTo'],
      value: [router.query?.dateFrom || null, router.query?.dateTo || null],
      onChange: handleDateRangeChange,
    },
  ];

  return (
    <PinnedFilters
      pinnedFilters={pinnedFilters}
      filtersConfig={FILTERS_CONFIG}
      handleChangeSingleFilter={handleChangeSingleFilter}
      handleChangeMultiFilter={handleChangeMultiFilter}
    />
  );
};

export default TradeTicketsFilters;
